import React from 'react';
import BotPageLayout from '../../components/BotPageLayout';
import { IconCode, IconCalendar, IconTerminal, IconGlobe, IconClock, IconLightning, IconBox } from '../../components/Icons';
import './CodeAssist.css';

const FACTS = [
  { Icon: IconCalendar, label: 'Launched',     value: '2026' },
  { Icon: IconTerminal, label: 'Commands',     value: '10+'  },
  { Icon: IconGlobe,    label: 'Languages',    value: '20+'  },
  { Icon: IconClock,    label: 'Avg response', value: '<2s'  },
];

const LANGS = ['Python','JavaScript','TypeScript','Rust','Go','Java','C','C++','C#',
               'Ruby','PHP','Swift','Kotlin','HTML','CSS','SQL','Bash','R','Lua','Dart'];

function CheckItem({ children }) {
  return (
    <li className="ca-check-item">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="8" fill="var(--blue-light)"/>
        <path d="M4.5 8.5l2.2 2.2 4-5" stroke="var(--blue-dark)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {children}
    </li>
  );
}

export default function CodeAssistAbout() {
  return (
    <BotPageLayout
      botName="Code Assist"
      botSlug="code-assist"
      botIcon={<IconCode size={28} color="var(--blue-dark)" />}
      botTagline="Write, debug, and optimise code without leaving your Discord server."
    >
      <div className="container">

        {/* ── Main two-column layout: text + quick facts only ── */}
        <div className="ca-about animate-fade-up">
          <div className="ca-about__main">
            <span className="eyebrow">About Code Assist</span>
            <h2 className="section-title">What is Code Assist?</h2>
            <div className="rule" />

            <p className="ca-about__lead">
              Code Assist is a Discord bot launched in 2026 that brings real, AI-powered
              coding assistance directly into your server — no browser tabs, no
              context-switching, no external tools required.
            </p>

            <p className="ca-about__body">
              Unlike bots that rely on third-party AI APIs, Code Assist runs on a
              fully custom AI model built and managed by PERM Systems. This model
              is trained specifically for coding tasks — debugging, explanation,
              optimisation, and code generation — making it more focused and reliable
              than a general-purpose language model.
            </p>

            <p className="ca-about__body">
              Code Assist was built to serve developers where they already are. Discord
              is where coding communities live — so the tooling should live there too.
            </p>

            <h3 style={{ marginTop: 32, marginBottom: 14, fontSize: '1.05rem', fontWeight: 700 }}>
              Who is it for?
            </h3>
            <ul className="ca-about__bullets">
              <CheckItem><strong>Students</strong> learning to code who need instant explanations and error help.</CheckItem>
              <CheckItem><strong>Professional developers</strong> wanting a fast, focused code review in chat.</CheckItem>
              <CheckItem><strong>Educators</strong> running coding communities, bootcamps, or study servers.</CheckItem>
              <CheckItem><strong>Hobbyists</strong> building games, scripts, automation, and Discord bots.</CheckItem>
            </ul>

            <div className="ca-ai-note">
              <div className="ca-ai-note__icon">
                <IconLightning size={16} color="var(--blue-dark)" />
              </div>
              <div>
                <strong>Proprietary AI</strong>
                <p>
                  Code Assist uses a custom AI model owned and operated by PERM Systems.
                  It is not powered by OpenAI, Anthropic, or any third-party provider.
                  This gives PERM Systems full control over performance, safety, and
                  future capabilities.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar — Quick Facts only, keeps it clean */}
          <div className="ca-about__sidebar">
            <div className="ca-facts">
              <h4 className="ca-facts__title">Quick Facts</h4>
              {FACTS.map(({ Icon, label, value }) => (
                <div key={label} className="ca-fact">
                  <div className="icon-wrap icon-wrap--sm"><Icon size={15} /></div>
                  <div>
                    <p className="ca-fact__label">{label}</p>
                    <p className="ca-fact__value">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Desktop app teaser — full width below ── */}
        <div className="ca-leak-full animate-fade-up">
          <div className="ca-leak-full__header">
            <div className="ca-leak__dot" />
            <span className="ca-leak__label">Coming Soon</span>
          </div>
          <div className="ca-leak-full__body">
            <div className="ca-leak-full__icon">
              <IconBox size={28} color="var(--blue-dark)" />
            </div>
            <div className="ca-leak-full__text">
              <h3 className="ca-leak-full__title">Code Assist — Desktop App</h3>
              <p className="ca-leak-full__desc">
                An AI coding companion that lives alongside your editor — helping you
                write, fix, and understand code in real time as you type. No Discord required.
                Powered by the same custom PERM AI model as the bot.
              </p>
            </div>
            <div className="ca-leak-full__badge">Stay tuned</div>
          </div>
        </div>

        {/* ── Supported languages — full width, wraps naturally ── */}
        <div className="ca-langs-section animate-fade-up">
          <h4 className="ca-facts__title">Supported Languages</h4>
          <div className="ca-tags">
            {LANGS.map(l => <span key={l} className="ca-tag">{l}</span>)}
          </div>
        </div>

      </div>
    </BotPageLayout>
  );
}